import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { combineLatest, filter, finalize, forkJoin, iif, map, Observable, of, switchMap, takeUntil } from 'rxjs';
import _cloneDeep from 'lodash/cloneDeep';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { FormioAppConfig, FormioComponent, ViewerComponent } from 'dp-formio';
import { CustomEditGridComponent } from 'dp-formio';
import { BaseComponent } from '../../../base.component';
import { Process } from '../../../models/process.model';
import { ICriterion } from '../../../../distribute-team-members/models/criterion';
import { DecisionInput } from '../../../models/decision-input.model';
import { ExecuteVisitType } from '../../../models/execute-visit.model';
import { CriterionsService } from '../../../services/criterions.service';
import { CopyModel } from '../../../models/copy.model';
import { CriterionInformation } from '../../../models/criterion-information.mode';
import { showToast } from '../../../../toast.helper';
import { EditGridRequiredValidator, EvidencesRequiredValidator, MasarAnswerValidator, MasarSubAnswerValidator } from '../../../validators';
import { MasarCriterionsService } from '../../../services/masar-criterions.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';

export interface AttachmentItem {
  selected: boolean;
  file?: any;
  fileName: string;
}

@Component({
  selector: 'app-masar-question-decision',
  templateUrl: './masar-question-decision.component.html',
  styleUrls: ['./masar-question-decision.component.scss']
})
export class MasarQuestionDecisionComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() memberId: string;
  @Input() decisionPreview: boolean;
  @ViewChild(FormioComponent) formioComponent: FormioComponent;
  isLoadingResults: boolean;
  documents: any[];
  process: Process;
  process$ = this.criterionsService.process$;
  question$ = this.criterionsService.questionResult$;
  criterion$ = this.criterionsService.criterion$;
  answers$: Observable<Array<{ value: string, tooltipAr: string, tooltipEn: string, hasTooltip: boolean }>>;
  form: FormGroup;

  criterion: ICriterion;
  decisionInput: DecisionInput;
  modelId: number;
  lastSubAnswers: any[];

  get displayedColumns(): string[] {
    return this.isFormDisabled ? ['fileName'] : ['selected', 'fileName']
  };

  get subAnswers() { return this.form?.get("subAnswers") as FormArray; }
  get evidences() { return this.form?.get("evidences") as FormArray; }
  get isSessionDetails() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.SessionDetails;
  }

  get isInstitutionEntries() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries;
  }

  get isFinalReportWithInstitutionEntries() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }

  get isFinalReport() {
    return !this.decisionPreview && (this.decisionInput.executeVisitType == ExecuteVisitType.FinalReport ||
      this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries ||
      this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries);
  }

  get enabledCopy() {
    return !!this.memberId;
  }

  get enabledCopyToExecutiveSummary() {
    return this.masarCriterionsService.allCriterionEvaluated$.value &&
      this.decisionInput.enableEdit &&
      this.decisionInput.executeVisitType == ExecuteVisitType.FinalReport;
  }

  get isFormDisabled() {
    return !this.decisionInput?.enableEdit ||
      this.enabledCopy ||
      this.isInstitutionEntries ||
      this.isFinalReportWithInstitutionEntries ||
      !this.criterion$.value.allQuestionsAnswered ||
      this.isSessionNotStarted;
  }
  get isSessionNotStarted() {
    return !!this.decisionInput?.visitMetaData?.sessionChecked && !this.decisionInput?.visitMetaData?.sessionChecked?.sessionStarted;
  }

  constructor(private formBuilder: FormBuilder,
    private criterionsService: CriterionsService,
    private masarCriterionsService: MasarCriterionsService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private formioAppConfig: FormioAppConfig,
    private dialog: MatDialog) { super(); }

  ngOnInit() {
    this.initAnswers();
    combineLatest([
      this.criterionsService.criterion$
        .pipe(filter(data => data.criterionType == 'STICK')),
      this.criterionsService.decisionInputs$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([criterion, inputs]) => {
        this.criterion = criterion;
        this.decisionInput = inputs;
        this.subAnswers?.clear();
        this.evidences?.clear();
        this.form?.reset();
        this.initForm();
        this.registerUpdateFormValidation();
        this.getData();
        this.registerCopyEvaluation();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.memberId?.previousValue && changes.memberId?.previousValue != changes.memberId?.currentValue) {
      this.getData();
    }
  }

  copy(controlName: string) {
    const value = this.form.controls[controlName].value;
    if (this.enabledCopyToExecutiveSummary)
      this.criterionsService.sendToExecutiveSummary$.next(new CopyModel(controlName, value));
    else {
      this.criterionsService.sendEvaluation$.next(new CopyModel(controlName, value));
      if (controlName == 'evaluationJustifications') {
        this.criterionsService.sendEvaluation$.next(new CopyModel('evidences', this.evidences.value));
      }
    }
  }

  submit() {
    if (this.form.valid) {
      this.isLoadingResults = true;
      const value = _cloneDeep(this.form.value);
      delete value.question;

      const data: CriterionInformation = {
        id: this.modelId,
        criterionId: this.criterion.id,
        criterionKey: this.criterion.criterionKey,
        visitKey: this.decisionInput.visitMetaData.visitKey,
        memberId: this.decisionInput.visitMetaData.memberId,
        ...value,
        isApply: !value.notApply,
        evidences: value.evidences.filter(q => q.selected).map(item => { return { files: [item.file], fileName: item.fileName }; }),
        subAnswers: JSON.stringify(value.subAnswers)
      };

      of(!!this.modelId)
        .pipe(
          switchMap((isUpdate) =>
            iif(
              () => isUpdate,
              this.masarCriterionsService.editCriterionInformation(data, this.isFinalReport),
              this.masarCriterionsService.createCriterionInformation(data, this.isFinalReport)
            )
          ),
          finalize(() => this.isLoadingResults = false)
        )
        .subscribe(model => {
          this._updateForm(model, this.documents);
          this.criterionsService.decisionTaken$.next();
          showToast(this.toastrService,
            this.translateService.instant("forms.common.Criteria.Saved done"),
            this.translateService.instant("forms.common.Criteria.Saved Successfully"),
            false
          );
        });
    }
  }

  toggleAllEvidences(checked: boolean) {
    this.evidences.controls.forEach((control) => {
      control.get('selected').setValue(checked);
    });
  }

  openFile(file: any) {
    this.dialog.open(ViewerComponent, {
      position: {
        top: '0',
        right: '0',
      },
      data: {
        dir: this.translateService.currentLang == 'ar' ? 'rtl' : 'ltr',
        data: file,
        appUrl: this.formioAppConfig.formio.appUrl,
        formId: this.formioAppConfig.keys.attachmentsId
      },
      height: '100vh',
      width: '100vw',
      panelClass: 'full-screen-modal',
    });
  }

  private initForm() {
    this.form = this.formBuilder.group({
      question: [this.question$],
      notApply: [{ value: false, disabled: this.isFormDisabled }],
      evaluationJustifications: [{ value: '', disabled: this.isFormDisabled }, [Validators.required]],
      evidences: this.formBuilder.array([]),
      subAnswers: this.formBuilder.array([])
    }, {
      validators: [MasarAnswerValidator()]
    });
  }
  private registerUpdateFormValidation() {
    const controls = ['evaluationJustifications'];
    this.form.controls.notApply.valueChanges.subscribe(checked => {
      this.subAnswers.clear();
      controls.forEach(key => {
        if (!checked) {
          this.form.get(key).setValidators([Validators.required]);
        } else {
          this.form.get(key).reset();
          this.form.get(key).clearValidators();
          this.lastSubAnswers = [];
        }
        this.form.get(key).updateValueAndValidity();
      });
    });
    this.form.controls.evaluationJustifications.valueChanges.subscribe(value => {
      if (!!value) {
        this.evidences.setValidators([]);
      } else {
        const evidencesValue = this.evidences.value;
        evidencesValue.forEach(evidence => { evidence.selected = false; });
        this.evidences.setValue(evidencesValue);
        this.evidences.clearValidators();
      }
      this.evidences.updateValueAndValidity();
    });
  }

  private initAnswers() {
    this.answers$ = combineLatest([this.question$, this.criterionsService.process$])
      .pipe(
        filter(([question, process]) => !!question && !!process),
        map(([question, process]) => {
          this.process = process;
          this.subAnswers.clear();
          question.evaluationItems.forEach(q => {
            this.subAnswers.push(this.formBuilder.group({
              evaluationItem: [q],
              id: [{ value: q.id, disabled: this.isFormDisabled }, [Validators.required]],
              score: [{ value: this.lastSubAnswers?.find(sa => sa.evaluationItem.id == q.id)?.score, disabled: this.isFormDisabled }, []]
            }, {
              validators: [MasarSubAnswerValidator()]
            }));
          });

          if (process.answerMaxScore) {
            const generalToolTip = question.answerTooltip ? question.answerTooltip.find(q => !q.score) : { tooltip: '' };
            return Array.from({ length: process.answerMaxScore }, (_, i) => {
              const answerTooltip = generalToolTip ?? question.answerTooltip.find(q => q.score == i + 1);
              return {
                value: `${i + 1}`,
                tooltipAr: answerTooltip?.tooltipAr,
                tooltipEn: answerTooltip?.tooltipEn,
                hasTooltip: !!answerTooltip?.tooltipAr || !!answerTooltip?.tooltipEn
              }
            });
          }
        }));
  }

  private getData() {
    this.isLoadingResults = true;
    forkJoin([
      this.masarCriterionsService.getCriterionInformation(
        this.decisionInput.visitMetaData.visitKey,
        this.criterion.criterionKey,
        this.memberId || this.decisionInput.currentMemberId,
        this.isFinalReport),
      this.isFormDisabled ?
        of(null) :
        this.masarCriterionsService.getDocuments(this.criterion.id)
    ]).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isLoadingResults = false;
      })
    ).subscribe((results) => {
      this._updateForm(results[0], results[1]);
    });
  }
  private _updateForm(data: CriterionInformation, documents: any[]) {
    let files;
    if (documents?.length) {
      this.documents = documents;
      files = documents.map((attachment) => {
        const selectedDocument = data?.evidences?.find(q => q.files[0].key == attachment?.file[0].key) || false;
        return this.formBuilder.group({
          selected: [{ value: !!selectedDocument, disabled: this.isFormDisabled }],
          fileName: [attachment.fileName],
          file: attachment.file
        });
      });
    } else {
      files = data?.evidences?.map((attachment) => {
        return this.formBuilder.group({
          selected: [{ value: true, disabled: this.isFormDisabled }],
          fileName: [attachment.fileName],
          file: attachment.files
        });
      }) || [];
    }


    this.modelId = data?.id;
    this.form.patchValue({
      notApply: !data ? false : !data?.isApply,
      evaluationJustifications: data?.evaluationJustifications || '',
    });
    this.form.setControl('evidences', new FormArray(files));
    this.lastSubAnswers = data?.subAnswers ? JSON.parse(data.subAnswers) : [];
    this.initAnswers();
  }

  private registerCopyEvaluation() {
    if (!this.enabledCopy)
      this.criterionsService.sendEvaluation$
        .pipe(
          filter(data => !!data),
          takeUntil(this.destroy$),
        )
        .subscribe(data => {
          if (data.key == 'evidences') {
            data.value?.forEach(item => {
              item.file?.forEach(file => {
                delete file.id;
              });
            });
          }
          this.form.patchValue({
            [data.key]: data.value
          });
        });

  }

}